import React, { useEffect, useState } from "react";
import { ArrowUp, ArrowDown, Film, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { exportVideos, isVideoOut, mediaUrl, brand } from "@/lib/api";

const FMTS = [["mp4", "MP4 (H.264)"], ["mov", "MOV (ProRes)"], ["png_seq", "PNG sequence"], ["tga_seq", "TGA sequence"]];

// Pick finished videos, put them in order and export them joined as one file.
export default function StitchDialog({ open, onOpenChange, items }) {
  const videos = items.filter((g) => g.status === "succeeded" && isVideoOut(g, 0));
  const [order, setOrder] = useState([]);
  const [fmt, setFmt] = useState("mp4");
  useEffect(() => { if (open) setOrder([]); }, [open]);
  const toggle = (id) => setOrder((o) => (o.includes(id) ? o.filter((x) => x !== id) : [...o, id]));
  const move = (id, d) => setOrder((o) => { const a = [...o]; const i = a.indexOf(id); const j = i + d; if (j < 0 || j >= a.length) return a; [a[i], a[j]] = [a[j], a[i]]; return a; });
  const byId = Object.fromEntries(videos.map((g) => [g.id, g]));
  const go = () => { exportVideos(order.map((id) => ({ generation_id: id, i: 0 })), fmt); onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0D12] border-zinc-800 text-zinc-100 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="stitch-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading text-xl flex items-center gap-2"><Film className="w-5 h-5 text-[#41a7f5]" />Stitch & Export</DialogTitle></DialogHeader>
        <p className="text-xs text-zinc-400 -mt-2">Tick the videos to join, arrange them in order, then export them as one file.</p>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="stitch-pick">
          {videos.map((g) => {
            const n = order.indexOf(g.id);
            return (
              <button key={g.id} type="button" onClick={() => toggle(g.id)} data-testid={`stitch-item-${g.id}`}
                className={`relative rounded-lg overflow-hidden border text-left ${n >= 0 ? "border-[#1e97f3]" : "border-zinc-800"}`}>
                <video src={mediaUrl(g.output_urls[0])} muted preload="metadata" className="w-full aspect-video object-cover bg-black" />
                {n >= 0 && <span className="absolute top-1 left-1 h-5 min-w-5 px-1 rounded-full bg-[#1e97f3] text-white text-[10px] font-bold flex items-center justify-center">{n + 1}</span>}
                <div className="p-1.5 text-[10px] text-zinc-400 truncate">{brand(g.model_name)}</div>
              </button>
            );
          })}
          {!videos.length && <p className="col-span-full text-sm text-zinc-500">No finished videos in this section yet.</p>}
        </div>
        {!!order.length && (
          <div className="space-y-1" data-testid="stitch-order">
            <div className="text-xs text-zinc-300">Order</div>
            {order.map((id, i) => (
              <div key={id} className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-[#0D1016] p-1.5" data-testid={`stitch-order-${i}`}>
                <span className="w-5 text-center text-xs font-mono text-zinc-400">{i + 1}</span>
                <span className="flex-1 text-xs truncate">{(byId[id]?.prompt || brand(byId[id]?.model_name) || "").slice(0, 90)}</span>
                <button type="button" onClick={() => move(id, -1)} disabled={!i} className="ax-icon-chip disabled:opacity-30" data-testid={`stitch-up-${i}`}><ArrowUp className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => move(id, 1)} disabled={i === order.length - 1} className="ax-icon-chip disabled:opacity-30" data-testid={`stitch-down-${i}`}><ArrowDown className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-1.5" data-testid="stitch-formats">
          {FMTS.map(([f, l]) => <button key={f} type="button" onClick={() => setFmt(f)} data-testid={`stitch-fmt-${f}`}
            className={`ax-chip ${fmt === f ? "!border-[#1e97f3] text-white" : ""}`}>{fmt === f && <Check className="w-3 h-3" />}{l}</button>)}
        </div>
        <button type="button" onClick={go} disabled={!order.length} data-testid="stitch-export"
          className="h-11 rounded-lg bfx-gradient-bg text-white text-sm font-semibold disabled:opacity-40">
          {order.length > 1 ? `Stitch ${order.length} videos & export` : order.length ? "Export video" : "Pick videos to export"}</button>
      </DialogContent>
    </Dialog>
  );
}
